import React, { useState, useEffect } from 'react';
import { API_PATHS } from '../../../utils/constants';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  Divider,
  Grid,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '../../../services/auth';

// Status chip component
const StatusChip = ({ status }) => {
  let color = 'default';
  let label = status;
  
  switch (status) {
    case 'COMPLETED':
      color = 'success';
      label = 'Completed';
      break;
    case 'IN_PROGRESS':
      color = 'warning';
      label = 'In Progress';
      break;
    case 'CANCELLED':
      color = 'error';
      label = 'Cancelled';
      break;
    case 'CREATED':
      color = 'info';
      label = 'Created';
      break;
    case 'PENDING':
      color = 'default';
      label = 'Pending';
      break;
    case 'OPEN':
      color = 'warning';
      label = 'Open';
      break;
    default:
      color = 'default';
      label = status;
  }
  
  return (
    <Chip 
      label={label} 
      color={color} 
      size="small"
      sx={{
        fontWeight: 'medium',
        '& .MuiChip-label': {
          px: 2
        }
      }}
    />);
};

const WorkflowRequestTickets = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInstance = async () => {
      try {
        setLoading(true);
        // Get the auth token
        const token = await getToken();
        const config = { headers: { Authorization: token } };
        const apiBaseUrl = API_PATHS.API;
        
        // Fetch workflow instance
        const response = await axios.get(`${apiBaseUrl}/workflow-instances/${id}`, config);
        const data = response.data;

        if (!data) {
          console.error('Workflow instance not found');
          navigate('/CST-Workflow/requests');
          return;
        }

        // Fetch tickets for this workflow instance
        const ticketsResponse = await axios.get(`${apiBaseUrl}/tickets?workflowInstanceId=${id}`, config);
        const ticketsData = ticketsResponse.data;

        // Transform tickets into steps
        const steps = (ticketsData.tickets || []).map(ticket => ({
          workflowStepId: ticket.workflowStepId,
          ticketId: ticket.id,
          name: ticket.stepName || ticket.workflowStepName,
          serviceName: ticket.serviceName,
          status: ticket.status,
          assignedTo: ticket.assignedToName,
          stepNumber: ticket.workflowStepId?.split('-')[1] || '0',
          selfAssigned: ticket.selfAssigned || false,
          createdAt: ticket.createdAt,
          isActive: ticket.status === 'IN_PROGRESS'
        }));
        
        // Sort steps by stepNumber
        steps.sort((a, b) => {
          const aNum = parseInt(a.stepNumber) || 0;
          const bNum = parseInt(b.stepNumber) || 0;
          return aNum - bNum;
        });

        // Combine instance data with tickets
        const instanceWithTickets = {
          ...data,
          steps: steps,
          status: data.status || 'CREATED',
          hasActiveTickets: steps.some(step => step.status === 'IN_PROGRESS'),
          completedSteps: steps.filter(step => step.status === 'COMPLETED').length,
          totalSteps: steps.length
        };

        setInstance(instanceWithTickets);
        console.log('Fetched instance with tickets:', instanceWithTickets);
      } catch (error) {
        console.error('Error fetching workflow instance:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInstance();
  }, [id, navigate]);

  const handleBack = () => {
    navigate('/CST-Workflow/requests');
  };

  const handleViewTicket = (ticketId) => {
    // In a real implementation, this would navigate to the ticket detail page
    console.log('View ticket:', ticketId);
    // navigate(`/CST-Workflow/tickets/${ticketId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!instance) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Request not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to Requests
        </Button>
      </Box>
    );
  }

  const completedTickets = instance.steps.filter(step => step.status === 'COMPLETED').length;
  const totalTickets = instance.steps.length;
  const progress = totalTickets > 0 ? (completedTickets / totalTickets) * 100 : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          Tickets for Request #{instance.id}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6">
              {instance.requestTitle}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Workflow: {instance.workflowName}
            </Typography>
          </Box>
          <StatusChip status={instance.status} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Request ID
            </Typography>
            <Typography variant="body1">
              {instance.id}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Created Date
            </Typography>
            <Typography variant="body1">
              {new Date(instance.createdAt).toLocaleDateString()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Created By
            </Typography>
            <Typography variant="body1">
              {instance.createdBy}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Priority
            </Typography>
            <Typography variant="body1">
              {instance.priority}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Overall Progress
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {completedTickets} of {totalTickets} tickets completed
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ height: 8, borderRadius: 5 }} 
            color="primary"
          />
        </Box>

        <Typography variant="h6" gutterBottom>
          All Tickets
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Workflow Information
          </Typography>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    Workflow Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {instance.workflowName || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    Workflow Type
                  </Typography>
                  <Typography variant="body1">
                    {instance.workflowType || 'Standard'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    Total Steps
                  </Typography>
                  <Typography variant="body1">
                    {instance.steps?.length || 0}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>

        <Typography variant="subtitle1" gutterBottom>
          Tickets in this Workflow
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ticket ID</TableCell>
                <TableCell>Step Name</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Workflow Position</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {instance.steps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No tickets found for this request.
                  </TableCell>
                </TableRow>
              ) : (
                instance.steps.map((ticket, index) => (
                  <TableRow 
                    key={ticket.workflowStepId || index}
                    sx={{
                      backgroundColor: ticket.status === 'IN_PROGRESS' ? 'rgba(255, 193, 7, 0.08)' : 'inherit'
                    }}
                  >
                    <TableCell>{ticket.ticketId || 'Not generated'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: ticket.status === 'IN_PROGRESS' ? 'bold' : 'regular' }}>
                        {ticket.name}
                      </Typography>
                      {ticket.description && (
                        <Typography variant="caption" color="textSecondary">
                          {ticket.description}
                        </Typography>
                      )}
                      {ticket.createdAt && (
                        <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
                          Created: {new Date(ticket.createdAt).toLocaleString()}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: ticket.status === 'IN_PROGRESS' ? 'bold' : 'regular' }}>
                        {ticket.serviceName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`Step ${ticket.stepNumber || index + 1}`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontWeight: 'medium' }}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusChip status={ticket.status} />
                    </TableCell>
                    <TableCell>
                      {ticket.assignedTo ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">
                            {ticket.selfAssigned ? 'Self-assigned' : ticket.assignedTo}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Unassigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {ticket.ticketId && (
                        <IconButton
                          color="primary"
                          onClick={() => handleViewTicket(ticket.ticketId)}
                          title="View Ticket"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default WorkflowRequestTickets;
